import "./select-report-to-download.scss";

import React from "react";
import {cx} from "emotion";

import {cs} from "../../../../../react/chain-services";
import {UseState} from "../../../../../react/use-state";
import {Static2} from "../../../../../react/static-2";
import {AnyAction2} from "../../../../../react/any-action-2";
import {downloadBlob} from "../../../../../utils/browser/download-service";
import {createDownloadTileData} from "../../data/download-tile-data";
import {consumeContext} from "../../../../../react/context";

import {IframeDownloadModal} from "../../../../charts/common/iframe-modal";

import {ConfirmDialog} from "../../../../../../web-client/src/routes/common/confirm-dialog/confirm-dialog";

import {DownloadMenuItem} from "./download-menu-item";
import {DownloadPdf} from "./download-pdf";
import {downloadWithToast} from "@common/ui-components/toast/downloadWithToast";

export const SelectReportToDownload = ({collection, getTileFilters, isLiveDashboard, showPdfDownload, next}) =>
    cs(
        consumeContext("apis"),
        consumeContext("theme"),
        [
            "confirmDialog",
            (_, next) =>
                ConfirmDialog({
                    next,
                    submitText: "Close",
                    hideCancel: true,
                    title: "Error",
                }),
        ],
        ["iframeModal", (_, next) => IframeDownloadModal({next})],
        [
            "alertDownloadPDFModal",
            (_, next) =>
                !isLiveDashboard
                    ? ConfirmDialog({
                          next,
                          submitText: "Open Preview",
                          cancelText: "Close",
                          title: "Print PDF Not Available in Editor",
                          body: "The Print PDF function is not available in the editor due to formatting issues. To print a PDF please open the preview option and print the PDF from there.",
                      })
                    : next(),
        ],
        ["open", (_, next) => UseState({next})],
        ["ref", (_, next) => Static2({next})],
        ({ref, open}, next) =>
            AnyAction2({
                disabled: !open?.value,
                getDom: ref.get,
                fn: () => open.onChange(false),
                next,
            }),
        ({ref, open, apis, confirmDialog, iframeModal, alertDownloadPDFModal}) => {
            return next({
                show: () => {
                    open.onChange(true);
                },
                render: () =>
                    open?.value && (
                        <div className="select-report-to-download-a33" ref={ref.set}>
                            {showPdfDownload &&
                                DownloadPdf({
                                    dropdown: open,
                                    alertDownloadPDFModal,
                                    isLiveDashboard,
                                    collection,
                                    iframeModal,
                                })}

                            {collection.value.filterDownloadTiles.map((tile, index) =>
                                ReportTile({
                                    tile,
                                    key: index,
                                    getTileFilters,
                                    downloadData: createDownloadTileData({
                                        tileId: tile.id,
                                        collectionId: collection.value.id,
                                        apis,
                                    }),
                                    onClose: () => open.onChange(false),
                                    confirmDialog,
                                })
                            )}
                        </div>
                    ),
            });
        }
    );

const ReportTile = ({tile, getTileFilters, downloadData, confirmDialog, onClose}) =>
    cs(
        consumeContext("selectedTimezone"),
        consumeContext("downloadToast"),
        ["selectFormat", (_, next) => UseState({next})],
        ["disabled", (_, next) => UseState({next})],
        ({disabled, selectedTimezone, downloadToast}) => {
            const icons = {
                XLSX: <XLSXIcon />,
                CSV: <CSVIcon />,
                JSON: <JSONIcon />,
            };

            const formats = tile.style.downloadFormats.split(", ");

            const hasOneFormat = formats.length == 1;

            const onClickDownload = async (format) => {
                if (disabled.value) return;

                disabled.onChange(true);
                try {
                    //downloadWithToast(fetch, toast)
                    const {blob, filename} = await downloadWithToast({
                        fetch: () =>
                            downloadData({
                                format,
                                timeZoneId: selectedTimezone?.value,
                                filters: getTileFilters?.(tile.id).getValue(),
                            }),
                        toast: downloadToast,
                        _key: `${tile.id}${Date.now()}`,
                    });

                    if (blob) {
                        downloadBlob(blob, {
                            name: filename || `${tile.title}.${new Date().toISOString()}.${format.toLowerCase()}`,
                        });
                    }
                } catch (err) {
                    confirmDialog.show({
                        description: err.message,
                    });
                }
                disabled.onChange(false);
                onClose();
            };

            return DownloadMenuItem({
                label: (
                    <>
                        {tile.title} {tile.fieldValidity === "CannotDisplay" && "(No Data)"}
                    </>
                ),
                onClick: hasOneFormat ? () => onClickDownload(formats[0]) : null,
                otherCtrl:
                    tile.fieldValidity !== "CannotDisplay" && !hasOneFormat
                        ? ({downloadMenuCss, componentsStyle}) => (
                              <>
                                  <div className="arrow-right">
                                      <ArrowRightIcon />
                                  </div>

                                  <div
                                      className="format-choose"
                                      style={{
                                          borderRadius: componentsStyle.menuCornerRadius,
                                          overflow: "hidden",
                                      }}
                                  >
                                      {formats.map((format, index) => (
                                          <div
                                              className={cx("format-item", downloadMenuCss)}
                                              style={{
                                                  cursor: disabled.value ? "wait" : "",
                                              }}
                                              key={index}
                                              onClick={() => onClickDownload(format)}
                                          >
                                              <div className="icon">{icons[format]}</div>
                                              {format}
                                          </div>
                                      ))}
                                  </div>
                              </>
                          )
                        : null,
            });
        }
    );

const ArrowRightIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.9766 9.9994L7.85156 5.8744L9.0299 4.69607L14.3332 9.9994L9.0299 15.3027L7.85156 14.1244L11.9766 9.9994Z" fill="currentColor" />
    </svg>
);

const XLSXIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M16 2L21 7V21.008C20.9997 21.2712 20.895 21.5235 20.7088 21.7095C20.5226 21.8955 20.2702 22 20.007 22H3.993C3.73038 21.9982 3.47902 21.8931 3.29322 21.7075C3.10742 21.5219 3.00209 21.2706 3 21.008V2.992C3 2.444 3.445 2 3.993 2H16ZM13.2 12L16 8H13.6L12 10.286L10.4 8H8L10.8 12L8 16H10.4L12 13.714L13.6 16H16L13.2 12Z"
            fill="currentColor"
        />
    </svg>
);

const CSVIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M16 2.39844L21 7.39844V21.3914C21 21.9474 20.555 22.3984 20.007 22.3984H3.993C3.445 22.3984 3 21.9434 3 21.4064V3.39044C3 2.84244 3.447 2.39844 3.999 2.39844H16Z"
            fill="currentColor"
        />
        <path
            d="M9.34148 16.92C9.22815 16.9667 9.08148 17.0033 8.90148 17.03C8.72815 17.0633 8.56482 17.08 8.41148 17.08C7.88482 17.08 7.49815 16.98 7.25148 16.78C7.00482 16.5733 6.84148 16.2333 6.76148 15.76C6.68148 15.28 6.64148 14.5467 6.64148 13.56C6.64148 12.5867 6.68148 11.86 6.76148 11.38C6.84815 10.8933 7.01482 10.55 7.26148 10.35C7.50815 10.1433 7.89148 10.04 8.41148 10.04C8.59148 10.04 8.76482 10.0533 8.93148 10.08C9.10482 10.1067 9.23482 10.1433 9.32148 10.19V11.2C9.06148 11.1533 8.83148 11.13 8.63148 11.13C8.37148 11.13 8.18815 11.18 8.08148 11.28C7.98148 11.3733 7.91482 11.5767 7.88148 11.89C7.85482 12.2033 7.84148 12.76 7.84148 13.56C7.84148 14.36 7.85482 14.9167 7.88148 15.23C7.91482 15.5433 7.98148 15.75 8.08148 15.85C8.18815 15.9433 8.37148 15.99 8.63148 15.99C8.88482 15.99 9.12148 15.9667 9.34148 15.92V16.92ZM11.5439 17.08C11.3106 17.08 11.0572 17.0633 10.7839 17.03C10.5106 17.0033 10.2772 16.9633 10.0839 16.91V15.92C10.5706 15.9667 10.9472 15.99 11.2139 15.99C11.4006 15.99 11.5439 15.98 11.6439 15.96C11.7439 15.9333 11.8239 15.8867 11.8839 15.82C11.9772 15.7067 12.0239 15.46 12.0239 15.08C12.0239 14.7867 12.0106 14.58 11.9839 14.46C11.9639 14.34 11.9172 14.2533 11.8439 14.2C11.7706 14.14 11.6372 14.09 11.4439 14.05L10.6439 13.88C10.3906 13.8267 10.2172 13.6533 10.1239 13.36C10.0306 13.0667 9.98391 12.64 9.98391 12.08C9.98391 11.54 10.0506 11.1233 10.1839 10.83C10.3172 10.53 10.5072 10.3233 10.7539 10.21C11.0072 10.0967 11.3306 10.04 11.7239 10.04C12.0239 10.04 12.2939 10.06 12.5339 10.1C12.7806 10.14 12.9372 10.1733 13.0039 10.2V11.2C12.9372 11.1867 12.8139 11.1733 12.6339 11.16C12.4606 11.14 12.2739 11.13 12.0739 11.13C11.8606 11.13 11.6939 11.1467 11.5739 11.18C11.4606 11.2067 11.3772 11.2533 11.3239 11.32C11.2706 11.3867 11.2339 11.4733 11.2139 11.58C11.2006 11.6867 11.1939 11.84 11.1939 12.04C11.1939 12.2733 11.2006 12.4433 11.2139 12.55C11.2339 12.65 11.2706 12.7233 11.3239 12.77C11.3772 12.8167 11.4639 12.8533 11.5839 12.88L12.2739 13.04C12.4806 13.08 12.6506 13.15 12.7839 13.25C12.9239 13.35 13.0339 13.5233 13.1139 13.77C13.1872 14.01 13.2239 14.3767 13.2239 14.87C13.2239 15.4767 13.1606 15.94 13.0339 16.26C12.9139 16.58 12.7339 16.7967 12.4939 16.91C12.2606 17.0233 11.9439 17.08 11.5439 17.08ZM14.7655 17L13.7155 10.12H14.9355L15.6355 15.7H15.7255L16.4055 10.12H17.5955L16.5455 17H14.7655Z"
            fill="white"
        />
    </svg>
);

const JSONIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M16 2.39844L21 7.39844V21.3914C21 21.9474 20.555 22.3984 20.007 22.3984H3.993C3.445 22.3984 3 21.9434 3 21.4064V3.39044C3 2.84244 3.447 2.39844 3.999 2.39844H16Z"
            fill="currentColor"
        />
        <path
            d="M4.38207 15.97C4.6554 15.9633 4.84207 15.9367 4.94207 15.89C5.04207 15.8433 5.1054 15.7367 5.13207 15.57C5.1654 15.4033 5.18207 15.0933 5.18207 14.64V10.12H6.38207V14.02C6.38207 15.04 6.3454 15.7367 6.27207 16.11C6.19874 16.4833 6.0354 16.7233 5.78207 16.83C5.5354 16.9367 5.06874 16.9933 4.38207 17V15.97ZM8.76324 17.08C8.52991 17.08 8.27658 17.0633 8.00324 17.03C7.72991 17.0033 7.49658 16.9633 7.30324 16.91V15.92C7.78991 15.9667 8.16658 15.99 8.43324 15.99C8.61991 15.99 8.76324 15.98 8.86324 15.96C8.96324 15.9333 9.04324 15.8867 9.10324 15.82C9.19658 15.7067 9.24324 15.46 9.24324 15.08C9.24324 14.7867 9.22991 14.58 9.20324 14.46C9.18324 14.34 9.13658 14.2533 9.06324 14.2C8.98991 14.14 8.85658 14.09 8.66324 14.05L7.86324 13.88C7.60991 13.8267 7.43658 13.6533 7.34324 13.36C7.24991 13.0667 7.20324 12.64 7.20324 12.08C7.20324 11.54 7.26991 11.1233 7.40324 10.83C7.53658 10.53 7.72658 10.3233 7.97324 10.21C8.22658 10.0967 8.54991 10.04 8.94324 10.04C9.24324 10.04 9.51324 10.06 9.75324 10.1C9.99991 10.14 10.1566 10.1733 10.2232 10.2V11.2C10.1566 11.1867 10.0332 11.1733 9.85324 11.16C9.67991 11.14 9.49324 11.13 9.29324 11.13C9.07991 11.13 8.91324 11.1467 8.79324 11.18C8.67991 11.2067 8.59658 11.2533 8.54324 11.32C8.48991 11.3867 8.45324 11.4733 8.43324 11.58C8.41991 11.6867 8.41324 11.84 8.41324 12.04C8.41324 12.2733 8.41991 12.4433 8.43324 12.55C8.45324 12.65 8.48991 12.7233 8.54324 12.77C8.59658 12.8167 8.68324 12.8533 8.80324 12.88L9.49324 13.04C9.69991 13.08 9.86991 13.15 10.0032 13.25C10.1432 13.35 10.2532 13.5233 10.3332 13.77C10.4066 14.01 10.4432 14.3767 10.4432 14.87C10.4432 15.4767 10.3799 15.94 10.2532 16.26C10.1332 16.58 9.95324 16.7967 9.71324 16.91C9.47991 17.0233 9.16324 17.08 8.76324 17.08ZM12.9649 17.08C12.3849 17.08 11.9715 16.98 11.7249 16.78C11.4849 16.58 11.3315 16.2567 11.2649 15.81C11.2049 15.3567 11.1749 14.6067 11.1749 13.56C11.1749 12.5133 11.2049 11.7667 11.2649 11.32C11.3315 10.8667 11.4849 10.54 11.7249 10.34C11.9715 10.14 12.3849 10.04 12.9649 10.04C13.5449 10.04 13.9582 10.14 14.2049 10.34C14.4515 10.54 14.6049 10.8667 14.6649 11.32C14.7315 11.7667 14.7649 12.5133 14.7649 13.56C14.7649 14.6067 14.7315 15.3567 14.6649 15.81C14.6049 16.2567 14.4515 16.58 14.2049 16.78C13.9582 16.98 13.5449 17.08 12.9649 17.08ZM12.9649 15.99C13.1649 15.99 13.3015 15.95 13.3749 15.87C13.4482 15.79 13.4949 15.5933 13.5149 15.28C13.5415 14.9667 13.5549 14.3933 13.5549 13.56C13.5549 12.7267 13.5415 12.1533 13.5149 11.84C13.4949 11.5267 13.4482 11.33 13.3749 11.25C13.3015 11.17 13.1649 11.13 12.9649 11.13C12.7649 11.13 12.6282 11.17 12.5549 11.25C12.4882 11.33 12.4415 11.53 12.4149 11.85C12.3882 12.1633 12.3749 12.7333 12.3749 13.56C12.3749 14.3867 12.3882 14.96 12.4149 15.28C12.4415 15.5933 12.4882 15.79 12.5549 15.87C12.6282 15.95 12.7649 15.99 12.9649 15.99ZM18.1734 17L16.9034 13.12H16.8634V17H15.7134V10.12H16.8534L18.0434 13.73H18.1034V10.12H19.2434V17H18.1734Z"
            fill="white"
        />
    </svg>
);
